import React, { useMemo } from 'react';
import { Box, Flex, Link, Tooltip, Spinner } from '@chakra-ui/react';
import { Table } from '@/components/ats/Table/Table';
import { KText } from '@/ui-library/components/text';
import { type Requisition } from '../associateReq.interface';
import { StatusTag } from './RequisitionItem';
import { HeaderCell } from '@/components/ats/Table/tableCells';
import {
  requisitionTypeConfig,
  requisitionStatusConfig,
} from '../../constants/constants';
import { KTag } from '@/ui-library/components';
import { Trash01 } from '@kula-ai/icons/outline/Trash01';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { PartOfGroupTag } from '@/components/requisitions/PartOfGroupTag';
import { useQuery } from '@tanstack/react-query';
import { API_SERVICE, API_KEYS } from '../../api';

interface AssociatedReqListTableProps {
  requisitions: Requisition[];
  onDelete?: (requisition: Requisition) => void;
  includeStatus?: boolean;
  isDeleting?: boolean;
  isUpdating?: boolean;
  skipDelete?: boolean;
}

export const AssociatedReqListTable: React.FC<AssociatedReqListTableProps> = ({
  requisitions,
  onDelete,
  includeStatus = true,
  isDeleting = false,
  isUpdating = false,
  skipDelete = false,
}) => {
  const { jobPermissions } = useAtsPermissions();
  const sortedRequisitions = useMemo(() => {
    return [...requisitions].reverse();
  }, [requisitions]);

  // Check if any requisition is part of a group
  const hasGroupedRequisitions = useMemo(() => {
    return sortedRequisitions.some(
      (req) => req.requisition_group_id || (req as any).group?.id
    );
  }, [sortedRequisitions]);

  // Fetch all requisitions to get complete group data
  const { data: allRequisitionsData } = useQuery({
    queryKey: [
      API_KEYS.getRequisitions,
      { page: 1, limit: 500, sort_key: 'created_at', sort_order: 'desc' },
    ],
    queryFn: () =>
      API_SERVICE.getRequisitions({
        page: 1,
        limit: 500,
        sort_key: 'created_at',
        sort_order: 'desc',
      }),
    enabled: hasGroupedRequisitions,
    staleTime: 0,
  });

  // Merge fetched requisitions with provided allRequisitions
  const completeRequisitionsList = useMemo(() => {
    if (!hasGroupedRequisitions) {
      return requisitions;
    }

    const fetchedReqs = allRequisitionsData?.requisitions || [];

    // Create a map to deduplicate
    const reqMap = new Map();

    // Add fetched requisitions first (complete data)
    fetchedReqs.forEach((req: Requisition) => {
      reqMap.set(req.id, req);
    });

    // Add allRequisitions (may have some missing from fetch)
    requisitions.forEach((req) => {
      if (!reqMap.has(req.id)) {
        reqMap.set(req.id, req);
      }
    });

    return Array.from(reqMap.values());
  }, [hasGroupedRequisitions, allRequisitionsData, requisitions]);

  const tableKey = useMemo(
    () => sortedRequisitions.map((r) => r.id).join('-'),
    [sortedRequisitions]
  );

  const DeleteIconWrapper = React.useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      (props) => {
        if (isDeleting) {
          return (
            <Box {...props} display="inline-flex" alignItems="center">
              <Spinner
                size="sm"
                color="secondary-700"
                width="18px"
                height="18px"
              />
            </Box>
          );
        }
        return <Trash01 {...props} />;
      },
    [isDeleting]
  );

  const rowActionMenuItems = useMemo(
    () => [
      {
        type: 'mainAction',
        icon: DeleteIconWrapper,
        iconProps: isDeleting
          ? {}
          : {
              color: 'secondary-700',
              width: '18px',
              height: '18px',
              _hover: {
                color: 'error-700',
              },
            },
        onClick: (onClose, currentClickedRow) => {
          if (currentClickedRow?.has_association) {
            return;
          }
          onDelete?.(currentClickedRow);
        },
        enabled: true,
        isDisabled: (row) => row?.has_association || isDeleting,
        tooltip: (row) =>
          row?.has_association
            ? "This requisition can't be removed because it is associated with an active offer"
            : undefined,
      },
    ],
    [onDelete, isDeleting, DeleteIconWrapper]
  );

  // Calculate dynamic width for Req ID column based on actual content
  const reqIdColumnWidth = useMemo(() => {
    const CHAR_WIDTH = 8; // Average character width (8px for 14px font)
    const GAP_WIDTH = 8; // Gap between elements
    const PADDING = 40; // Column padding
    const MIN_WIDTH = 70; // Minimum column width

    // Calculate max req ID width
    const reqIds = sortedRequisitions?.map((req) => req.code).filter(Boolean);
    const maxReqIdWidth = reqIds?.length
      ? Math.max(...reqIds.map((id) => id.length * CHAR_WIDTH))
      : 0;

    // Check for tags presence
    const hasConfidential = sortedRequisitions?.some((req) => req.confidential);
    const hasPartOfGroup = sortedRequisitions?.some(
      (req) => req.requisition_group_id || (req as any).group?.id
    );

    // Start with req ID width
    let width = maxReqIdWidth;

    if (hasConfidential && hasPartOfGroup) {
      // Use combined width when both tags are present
      width +=
        GAP_WIDTH +
        ('Confidential requisition'.length / 2) * CHAR_WIDTH +
        GAP_WIDTH +
        'Part of a group'.length * CHAR_WIDTH;
    } else {
      // Add width for confidential tag if present
      if (hasConfidential) {
        width += GAP_WIDTH + 'Confidential requisition'.length * CHAR_WIDTH;
      }

      // Add width for part of group tag if present
      if (hasPartOfGroup) {
        width += GAP_WIDTH + 'Part of a group'.length * CHAR_WIDTH;
      }
    }

    return Math.max(width + PADDING, MIN_WIDTH);
  }, [sortedRequisitions]);

  // Calculate dynamic width for Status column based on actual content
  const statusColumnWidth = useMemo(() => {
    const dotWidth = 8;
    const gapWidth = 6;
    const statusLabels = sortedRequisitions
      ?.map((req) => {
        const statusConfig = requisitionStatusConfig[req.status];
        return statusConfig?.label || '';
      })
      .filter(Boolean) as string[];

    const maxStatusLabelWidth = statusLabels?.length
      ? Math.max(...statusLabels.map((label) => label.length * 8))
      : 0;

    const baseWidth =
      maxStatusLabelWidth > 0 ? dotWidth + gapWidth + maxStatusLabelWidth : 0;

    return Math.max(baseWidth + 40, 120);
  }, [sortedRequisitions]);

  const getTableColumns = useMemo(() => {
    return [
      {
        Header: <HeaderCell title="Req ID" />,
        accessor: 'code',
        width: reqIdColumnWidth,
        Cell: ({ value, row }) => {
          const { original } = row;
          const { confidential, status, id } = original;
          const reqStatus = status || '';
          const isDraft = reqStatus === 'draft';
          const reqMode = isDraft ? 'draft' : 'view';
          const reqId = id || '';
          const hasConfidential = sortedRequisitions?.some(
            (req) => req.confidential
          );
          const hasPartOfGroup = sortedRequisitions?.some(
            (req) => req.requisition_group_id || (req as any).group?.id
          );

          // Transform requisition to match PartOfGroupTag expected structure
          const transformedRequisition = {
            ...original,
            requisition_group_id:
              (original as any).group?.id || original.requisition_group_id,
            group_size: (original as any).group?.size || original.group_size,
          };

          // Transform all requisitions to have consistent structure
          const transformedAllRequisitions = completeRequisitionsList.map(
            (req) => ({
              ...req,
              requisition_group_id:
                (req as any).group?.id || req.requisition_group_id,
              group_size: (req as any).group?.size || req.group_size,
              code: req.code || (req as any).requisition_value,
            })
          );

          const confidentialTagWidth =
            hasConfidential && hasPartOfGroup ? '50%' : 'auto';

          return (
            <Flex
              alignItems="center"
              gap="8px"
              w="100%"
              minWidth="0"
              justifyContent="start !important"
            >
              {value && (
                <Link
                  href={`/requisitions?${reqMode}=${reqId}`}
                  target="_blank"
                  minWidth="0"
                >
                  <KText
                    size="sm-semi-bold"
                    color="primary-900"
                    _hover={{ textDecoration: 'underline' }}
                    isTruncated
                    enableTooltip={true}
                    tooltipText={value || ' '}
                    maxW="100%"
                  >
                    {value || ' '}
                  </KText>
                </Link>
              )}
              {!value && confidential && (
                <Tooltip
                  label="The requisition associated with this job is confidential"
                  placement="top"
                  hasArrow
                >
                  <Flex flexShrink={0} w={confidentialTagWidth}>
                    <KTag
                      value="Confidential requisition"
                      maxW="100%"
                      isTruncated
                      enableTooltip={true}
                    />
                  </Flex>
                </Tooltip>
              )}
              {value && confidential && (
                <Flex flexShrink={0} w={confidentialTagWidth}>
                  <KTag
                    value="Confidential requisition"
                    maxW="100%"
                    isTruncated
                    enableTooltip={true}
                  />
                </Flex>
              )}
              <PartOfGroupTag
                requisition={transformedRequisition as any}
                allRequisitions={transformedAllRequisitions as any}
              />
            </Flex>
          );
        },
      },
      includeStatus && {
        Header: <HeaderCell title="Status" textTransform="none" />,
        accessor: 'status',
        width: statusColumnWidth,
        Cell: ({ value }) => <StatusTag status={value} />,
      },
      {
        Header: <HeaderCell title="Role name" textTransform="none" />,
        accessor: 'role_name',
        width: 150,
        Cell: ({ value }) => (
          <Flex w="100%">
            <KText
              maxW="100%"
              isTruncated
              enableTooltip={true}
              tooltipText={value || ' '}
            >
              {value || '-'}
            </KText>
          </Flex>
        ),
      },
      {
        Header: <HeaderCell title="Requisition type" textTransform="none" />,
        accessor: 'requisition_type',
        width: 150,
        Cell: ({ value }) => (
          <KText>{requisitionTypeConfig[value]?.label || '-'}</KText>
        ),
      },
      {
        Header: <HeaderCell title="Target hire date" textTransform="none" />,
        accessor: 'target_hire_date',
        width: 150,
        Cell: ({ value }) => <KText>{value || '-'}</KText>,
      },
    ].filter(Boolean);
  }, [
    reqIdColumnWidth,
    includeStatus,
    statusColumnWidth,
    sortedRequisitions,
    completeRequisitionsList,
  ]);

  const canUpdate = useMemo(
    () => jobPermissions?.update ?? false,
    [jobPermissions?.update]
  );
  const canOffer = useMemo(
    () => jobPermissions?.offer ?? false,
    [jobPermissions?.offer]
  );

  const showRowActionMenu = canUpdate || canOffer;

  if (sortedRequisitions.length === 0) return null;

  return (
    <Box my={4} w="100%" position="relative">
      {isUpdating && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="whiteAlpha.800"
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="md"
        >
          <Spinner size="md" color="primary-700" />
        </Box>
      )}
      <Table
        key={tableKey}
        tableId="selected-requisitions"
        columns={getTableColumns}
        data={sortedRequisitions}
        skipPagination={true}
        rowActionMenuItems={showRowActionMenu && !skipDelete ? rowActionMenuItems : []}
        rowActionMenuStyle={{
          bg: 'unset',
          border: 'none',
          boxShadow: 'none',
        }}
      />
    </Box>
  );
};
